import { IonPage } from "@ionic/react";
import { Box } from "@mui/material";
import { LicenseDetails } from "@src/app/licenseManager/licenseDetails";
import { NeedHelpPage } from "@src/app/needhelp";
import HelpDetailsPage from "@src/app/needhelp/helpDetails";
import { ICAgreementPage } from "@src/appV2/Accounts/ICAgreement/Page";
import { ReferAndEarnPage } from "@src/appV2/Accounts/ReferAndEarn";
import { ReferAndEarnRouterPath } from "@src/appV2/Accounts/ReferAndEarn/paths";
import { ProfileAddressPage } from "@src/appV2/Address/components/ProfileAddressPage";
import { SIGN_NEW_AGREEMENT_PATH } from "@src/appV2/Agreements/paths";
import { RootPaths } from "@src/appV2/App/paths";
import { useShowInAppRatingPrompt } from "@src/appV2/App/useInAppRatingPrompt/useShowInAppRatingPrompt";
import { StepUpAuthProvider } from "@src/appV2/Auth/LoginSignUp/StepUpAuth/context";
import { CareerAgentFormPage } from "@src/appV2/CareerAgent/CareerAgentFormPage";
import { CHAT_PAGE_FULL_PATH } from "@src/appV2/Chat/paths";
import { useEmbeddedMode } from "@src/appV2/EmbeddedMode/EmbeddedModeContext";
import { RouteTransitionSwitch } from "@src/appV2/lib";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import { AccountsPage } from "@src/appV2/redesign/Accounts/Page";
import { ACCOUNTS_BASE_PATH, PROFILE_FULL_PATH } from "@src/appV2/redesign/Accounts/paths";
import { ACCOUNT_ROLES_FULL_PATH } from "@src/appV2/redesign/Accounts/Roles/paths";
import { ClipboardGuidesChatPage } from "@src/appV2/redesign/ClipboardGuides/ClipboardGuidesChatPage";
import { SignNewAgreementPage as SignNewAgreementPageV2 } from "@src/appV2/redesign/ICAgreement/SignNewAgreement/Page";
import { Navbar } from "@src/appV2/redesign/Navbar";
import {
  NOTIFICATIONS_PREFERENCE_PATH,
  NOTIFICATIONS_PREFERENCE_PATH_FULL,
} from "@src/appV2/redesign/NotificationPreferences/paths";
import { useSelectedWorkType } from "@src/appV2/redesign/Onboarding/hooks/useSelectedWorkType";
import { useIsSurfaceJobsEnabled } from "@src/appV2/redesign/Placements/hooks/useIsSurfaceJobsEnabled";
import { PLACEMENT_PATHS } from "@src/appV2/redesign/Placements/paths";
import { useIsJobsRedirectionEnabled } from "@src/appV2/redesign/Placements/useIsJobsRedirectionEnabled";
import { ProfilePage } from "@src/appV2/redesign/Profile/Page";
import {
  SHIFT_DISCOVERY_LIST_PATH,
  SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_REVIEWS_COMMENT_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_REVIEWS_MODAL_PATH,
  WORK_WITH_FRIENDS_PATH,
} from "@src/appV2/redesign/ShiftDiscovery/paths";
import {
  WorkplaceCommentModalRoutePathParams,
  WorkplaceModalRoutePathParams,
} from "@src/appV2/redesign/ShiftDiscovery/types";
import {
  MY_SHIFTS_FULL_PATH as MY_SHIFTS_FULL_PATH_REDESIGN,
  SHIFT_DETAILS_FULL_PATH,
  UNVERIFIED_SHIFTS_FULL_PATH,
} from "@src/appV2/redesign/WorkerShifts/path";
import { WorkplaceReviewsRouterPath } from "@src/appV2/redesign/Workplace/Review/paths";
import { WorkerBadgesPage } from "@src/appV2/WorkerBadges/Page";
import { ReactElement, useEffect } from "react";
import { Redirect, Route, generatePath, useLocation, useRouteMatch } from "react-router-dom";
import "./style.scss";

import { WorkerAppVersion } from "../appVersion/workerAppVersion";
import { LicenseReciprocityPage } from "../licenseManager/licenseReciprocity";
import { NotificationCenter } from "../notificationCenter/notificationCenter";
import { OnboardToStripe } from "../onboardingStripe";
import { OnboardingSuccess } from "../onboardingStripe/components/OnStripeSetUp";
import { SetUpStripe } from "../onboardingStripe/components/SetUpStripe";
import { StripeSetupPage } from "../onboardingStripe/StripeSetupPage";
import { PushNotificationComponent } from "../pushNotifications";
import { TabRouterPath } from "../routing/constant/tabRoute";

const NavigationPaths = new Set<string>([
  TabRouterPath.OPEN_SHIFTS,
  TabRouterPath.MY_SHIFTS,
  ACCOUNTS_BASE_PATH,
]);

export function AppTabs(): ReactElement {
  const match = useRouteMatch();
  const { pathname } = useLocation();
  const currentPathIsANavigationPath = NavigationPaths.has(pathname);

  const location = useLocation();

  const { requestInAppRatingIfTriggerEnabled } = useShowInAppRatingPrompt();

  const isJobsRedirectionEnabled = useIsJobsRedirectionEnabled();
  const { isEmbedded } = useEmbeddedMode();
  const isSurfaceJobsEnabled = useIsSurfaceJobsEnabled();
  const selectedWorkType = useSelectedWorkType();
  const showJobsFirst = isSurfaceJobsEnabled && selectedWorkType.isPermanentPlacementsOnboarding;

  useEffect(() => {
    void requestInAppRatingIfTriggerEnabled({
      triggerStrategy: {
        name: "on_route_open",
        route: location.pathname,
      },
    });
  }, [location.pathname, requestInAppRatingIfTriggerEnabled]);

  const { hcfProfileSource: hcfProfileSourceQueryParam } = useSearchParams();

  const navbar = currentPathIsANavigationPath ? <Navbar /> : null;

  return (
    <div className="cbhAppV1">
      <Route path={`${match.url}`} component={WorkerAppVersion} />
      <Route path={`${match.url}`} component={PushNotificationComponent} />

      <Box
        sx={{
          /**
           * The Box here ensures that IonPage is positioned in a new z-index stack.
           * Remove this Box when we eliminate `IonPage`.
           * Test that scrolling to the bottom of the content still works, even
           * on a small UI.
           */
          position: "absolute",
          display: "flex",
          flexDirection: "column",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <Box position="relative" flex="1 1 0">
          <RouteTransitionSwitch>
            {/* Redirect legacy routes to redesigned page */}
            <Route
              path={TabRouterPath.MY_SHIFTS}
              render={() => <Redirect to={MY_SHIFTS_FULL_PATH_REDESIGN} />}
              exact
            />
            <Route
              path={TabRouterPath.OPEN_SHIFTS}
              render={() => (
                <Redirect to={`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}`} />
              )}
              exact
            />

            <Route path={TabRouterPath.RATINGS_BADGES} component={WorkerBadgesPage} exact />

            <Route path={ACCOUNTS_BASE_PATH} component={AccountsPage} exact />
            <Route
              path={TabRouterPath.CHAT}
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: generatePath(CHAT_PAGE_FULL_PATH, {
                      facilityId: match.params.facilityId,
                    }),
                    search: location.search,
                  }}
                />
              )}
              exact
            />
            <Route
              path={TabRouterPath.CLIPBOARD_GUIDES_CHAT}
              component={ClipboardGuidesChatPage}
              exact
            />
            <Route path={TabRouterPath.ADDRESS} component={ProfileAddressPage} exact />
            <Route
              path={TabRouterPath.FACILITY_DETAIL}
              render={({ match }) => {
                const workplaceOpenShiftsModalPath = generatePath(
                  SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH,
                  { workplaceId: match.params.facilityId! }
                );

                const searchParams =
                  typeof hcfProfileSourceQueryParam === "string"
                    ? `?linkSource=${hcfProfileSourceQueryParam}`
                    : "";

                return (
                  <Redirect
                    to={`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}/${workplaceOpenShiftsModalPath}${searchParams}`}
                  />
                );
              }}
              exact
            />
            <Route
              exact
              path={TabRouterPath.UPDATED_CONTRACTOR_AGREEMENT}
              render={() => <OnboardToStripe />}
            />
            <Route path={TabRouterPath.NEED_HELP} component={NeedHelpPage} exact />
            <Route path={TabRouterPath.HELP_DETAILS} component={HelpDetailsPage} exact />
            <Route path={PROFILE_FULL_PATH} component={ProfilePage} exact />
            <Route path={TabRouterPath.PROFILE_CONTRACT} component={ICAgreementPage} exact />

            {/* Redirect legacy routes to redesigned pages */}
            <Route path={TabRouterPath.UNVERIFIED_SHIFTS} exact>
              <Redirect to={UNVERIFIED_SHIFTS_FULL_PATH} />
            </Route>
            <Route
              path={TabRouterPath.MY_SHIFT_DETAIL}
              render={({ match }) => (
                <Redirect
                  to={generatePath(SHIFT_DETAILS_FULL_PATH, { shiftId: match.params.shiftId })}
                />
              )}
            />

            <Route path={TabRouterPath.SUCCESS_ONBOARDING} component={OnboardingSuccess} exact />
            <Route path={TabRouterPath.PAYMENT_SERVICE_ONBOARDING} component={SetUpStripe} exact />
            <Route
              path={TabRouterPath.FRIENDS_LIST}
              render={() => <Redirect to={`${RootPaths.APP_V2_HOME}/${WORK_WITH_FRIENDS_PATH}`} />}
              exact
            />

            <Redirect
              from={`${match.url}/${NOTIFICATIONS_PREFERENCE_PATH}`}
              to={NOTIFICATIONS_PREFERENCE_PATH_FULL}
              exact
            />

            {/* License Manager Routes */}

            <Route path={TabRouterPath.LICENSE_MANAGER} exact>
              <Redirect to={ACCOUNT_ROLES_FULL_PATH} />
            </Route>
            <Route path={TabRouterPath.ADD_LICENSE} exact>
              <Redirect to={ACCOUNT_ROLES_FULL_PATH} />
            </Route>
            <Route path={TabRouterPath.LICENSE_DETAILS} component={LicenseDetails} exact />

            <Route
              path={TabRouterPath.LICENSE_RECIPROCITY}
              component={LicenseReciprocityPage}
              exact
            />

            <Route path={TabRouterPath.SETUP_STRIPE} component={StripeSetupPage} exact />

            <Route path={TabRouterPath.NOTIFICATION_CENTER} component={NotificationCenter} exact />

            {/* Career Agent Routes */}
            <Route path={TabRouterPath.CAREER_AGENT} component={CareerAgentFormPage} exact />

            {/**
             * Workplace Reviews Routes
             * FIXME: history.replace is not working as expecting because of bugs in IonRouterOutlet
             * history.replace does not function correctly when the route is in V2, so moving it here
             * https://www.loom.com/share/Clipboard-Health-15-September-2023-6e996f1559464c57acd2b7be64719719
             * Move this route to V2 once we move away from IonRouterOutlet
             */}
            <Route exact path={WorkplaceReviewsRouterPath.REVIEWS_SUMMARY_PATH}>
              <Route
                render={({ match }) => (
                  <Redirect
                    to={generatePath(
                      `${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}/${SHIFT_DISCOVERY_WORKPLACE_REVIEWS_MODAL_PATH}`,
                      match.params as unknown as WorkplaceModalRoutePathParams
                    )}
                  />
                )}
              />
            </Route>
            <Route exact path={WorkplaceReviewsRouterPath.REVIEW_COMMENT_REPLIES_PATH}>
              <Route
                render={({ match }) => (
                  <Redirect
                    to={generatePath(
                      `${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}/${SHIFT_DISCOVERY_WORKPLACE_REVIEWS_COMMENT_MODAL_PATH}`,
                      match.params as unknown as WorkplaceCommentModalRoutePathParams
                    )}
                  />
                )}
              />
            </Route>

            {/**
             * FIXME: history.replace is not working as expecting because of bugs in IonRouterOutlet
             * history.replace does not function correctly when the route is in V2, so moving it here
             * Move this route to V2 once we move away from IonRouterOutlet
             */}
            <Route exact path={ReferAndEarnRouterPath.REFER_AND_EARN_PATH}>
              <ReferAndEarnPage />
            </Route>
            <Route exact path={`${RootPaths.HOME}/${SIGN_NEW_AGREEMENT_PATH}`}>
              <IonPage>
                <StepUpAuthProvider>
                  <SignNewAgreementPageV2 />
                </StepUpAuthProvider>
              </IonPage>
            </Route>
            {/* Fallback */}
            <Route
              render={() => {
                if (showJobsFirst || isJobsRedirectionEnabled || isEmbedded) {
                  localStorage.setItem("jobs-view-mode", '"list"');
                  return <Redirect to={PLACEMENT_PATHS.ROOT} />;
                }
                return <Redirect to={`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}`} />;
              }}
            />
          </RouteTransitionSwitch>
        </Box>
        {navbar}
      </Box>
    </div>
  );
}
